import { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/api";

const PREVIEW_LIMIT_S = 180;

const fmt = (s) => {
  if (!s || s < 0) return "0:00";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

/**
 * StreamPreview — public 3-minute preview player for a stream product.
 * Props: product (with product_id, stream_type, chapters:[{index,title,duration_seconds,mime}])
 * Enforces 180s cap client-side by pausing on timeupdate.
 */
export default function StreamPreview({ product }) {
  const [ci, setCi] = useState(0);
  const [cutOff, setCutOff] = useState(false);
  const mediaRef = useRef(null);
  const chapters = product?.chapters || [];
  const chapter = chapters[ci];
  const isVideo = product?.stream_type === "video";

  useEffect(() => { setCutOff(false); }, [ci]);

  const onTimeUpdate = (e) => {
    if (e.target.currentTime >= PREVIEW_LIMIT_S) {
      e.target.pause();
      e.target.currentTime = PREVIEW_LIMIT_S;
      setCutOff(true);
    }
  };
  const onSeeking = (e) => {
    if (e.target.currentTime > PREVIEW_LIMIT_S) {
      e.target.currentTime = PREVIEW_LIMIT_S;
      setCutOff(true);
    }
  };

  if (!product || !chapters.length) return null;

  const src = `${BACKEND_URL}/api/streams/${product.product_id}/preview/${ci}`;

  return (
    <div className="brut-border bg-black overflow-hidden mt-6" data-testid="stream-preview-frame">
      {isVideo ? (
        <video
          key={`prev-v-${ci}`}
          ref={mediaRef}
          data-testid="stream-preview-video"
          controls
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          onTimeUpdate={onTimeUpdate}
          onSeeking={onSeeking}
          className="w-full aspect-video bg-black"
          src={src}
        />
      ) : (
        <div className="p-6 bg-[#1F3D2A] text-white">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">FREE 3-MIN PREVIEW</div>
          <div className="heading text-xl font-bold mt-2">{chapter.title}</div>
          <audio
            key={`prev-a-${ci}`}
            ref={mediaRef}
            data-testid="stream-preview-audio"
            controls
            controlsList="nodownload noremoteplayback"
            onContextMenu={(e) => e.preventDefault()}
            onTimeUpdate={onTimeUpdate}
            onSeeking={onSeeking}
            className="w-full mt-4"
            src={src}
          />
        </div>
      )}
      <div className="p-3 bg-[#FDF3E9] border-t-2 border-[#1F3D2A] flex flex-wrap items-center justify-between gap-3">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#555]">Previewing {chapters.length > 1 ? `chapter ${ci + 1} / ${chapters.length}` : `up to ${fmt(PREVIEW_LIMIT_S)}`} · Buy to unlock full playback</div>
        {chapters.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="preview-chapter-picker">
            {chapters.map((c, i) => (
              <button key={i} data-testid={`preview-chapter-${i}`} onClick={() => setCi(i)}
                className={`brut-border px-2 py-1 mono text-[9px] uppercase tracking-[0.25em] ${i === ci ? "bg-[#C77E3C] text-white" : "bg-white text-[#1F3D2A]"}`}>
                {String(i + 1).padStart(2, "0")}
              </button>
            ))}
          </div>
        )}
      </div>
      {cutOff && (
        <div data-testid="preview-cutoff-notice" className="p-3 bg-[#C77E3C] text-white mono text-[10px] uppercase tracking-[0.3em] text-center">
          — 3-minute preview ended — buy to unlock the rest
        </div>
      )}
    </div>
  );
}
